import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";

import StationLayout from "./components/StationLayout";
import { PlayerDock } from "./components/StreamPlayer";
import { StreamProvider } from "./contexts/StreamContext";
import AboutPage from "./pages/AboutPage";
import BetaPage from "./pages/BetaPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import DjPage from "./pages/DjPage";
import DjsList from "./pages/DjsList";
import DmcaPage from "./pages/DmcaPage";
import EpisodePage from "./pages/EpisodePage";
import EpisodesPage from "./pages/EpisodesPage";
import Homepage from "./pages/Homepage";
import NotFound from "./pages/NotFound";
import PrivacyPage from "./pages/PrivacyPage";
import RecentlyPlayed from "./pages/RecentlyPlayed";
import ShowPage from "./pages/ShowPage";
import ShowsList from "./pages/ShowsList";
import TermsPage from "./pages/TermsPage";

// Playback is site-wide: the provider owns the audio element and the dock
// renders it as a bottom bar on any page with no player of its own (every
// page but a station's About tab, the inline player's one home).
const RootLayout = () => (
  <StreamProvider>
    <Outlet />
    <PlayerDock />
  </StreamProvider>
);

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Homepage,
});

// Static segments outrank `$stationSlug`, so no station may be slugged
// "beta", "terms", "privacy", "dmca", or "delete-account" (slugs are
// hand-assigned; one that tried would just be shadowed).
const betaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/beta",
  component: BetaPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const dmcaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dmca",
  component: DmcaPage,
});

const deleteAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/delete-account",
  component: DeleteAccountPage,
});

/*
 * Everything public about a station hangs off its slug, under one layout
 * that owns the header, the player and the tabs. Lists and details nest,
 * and every :station and :show segment accepts a slug or an id:
 *
 *   /:station                                    about (default)
 *   /:station/recent                             recently played
 *   /:station/shows                              the shows tab
 *   /:station/shows/:show                        one show
 *   /:station/shows/:show/episodes               a show's full archive
 *   /:station/shows/:show/episodes/:episodeId    one episode (recording id)
 *   /:station/djs                                the DJs tab
 *   /:station/djs/:dj                            one DJ
 *
 * The pre-/shows/-era paths (/:station/:show, /:station/dj/:dj) are gone
 * from the client; Django 301s them to this grammar (core/seo.py).
 */
const stationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$stationSlug",
  component: StationLayout,
});

const aboutRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "/",
  component: AboutPage,
});

const recentlyPlayedRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "recent",
  component: RecentlyPlayed,
});

const showsRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "shows",
  component: ShowsList,
});

const showRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "shows/$showSlug",
  component: ShowPage,
});

const showEpisodesRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "shows/$showSlug/episodes",
  component: EpisodesPage,
});

const episodeRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "shows/$showSlug/episodes/$episodeId",
  component: EpisodePage,
});

const djsRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "djs",
  component: DjsList,
});

const djRoute = createRoute({
  getParentRoute: () => stationRoute,
  path: "djs/$djSlug",
  component: DjPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  betaRoute,
  termsRoute,
  privacyRoute,
  dmcaRoute,
  deleteAccountRoute,
  stationRoute.addChildren([
    aboutRoute,
    recentlyPlayedRoute,
    showsRoute,
    showRoute,
    showEpisodesRoute,
    episodeRoute,
    djsRoute,
    djRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
